const MAX_RETRIES = 3;
const BLOCK_DURATION = 120000; // 2 minutes
const MIN_RETRY_DELAY = 2000; // 2 seconds
const MAX_RETRY_DELAY = 30000; // 30 seconds
const FATAL_ERROR_BLOCK_DURATION = 300000; // 5 minutes for fatal errors

const retryTracking = new Map();
const errorTypeTracking = new Map();

const NON_RETRYABLE_ERRORS = [500, 501, 502, 503, 504];
const FATAL_ERRORS = ['NETWORK_ERROR', 'SERVER_ERROR', 'MAX_RETRIES_EXCEEDED'];

export const shouldRetryApiCall = (apiKey, errorStatus = null) => {
  const now = Date.now();
  const retryData = retryTracking.get(apiKey);

  // Block retries for server errors (500+) immediately
  if (errorStatus && NON_RETRYABLE_ERRORS.includes(errorStatus)) {
    markApiFailed(apiKey, 'SERVER_ERROR');
    return false;
  }
  
  // Block retries for network errors (0 = no response)
  if (errorStatus === 0) {
    markApiFailed(apiKey, 'NETWORK_ERROR');
    return false;
  }

  if (!retryData) {
    retryTracking.set(apiKey, {
      count: 1,
      lastAttempt: now,
      blocked: false,
      errorStatus: errorStatus || null
    });
    return true;
  }

  if (retryData.blocked) {
    const blockUntil = retryData.blockUntil || (retryData.lastAttempt + BLOCK_DURATION);
    if (now < blockUntil) {
      return false;
    }
    retryTracking.set(apiKey, {
      count: 1,
      lastAttempt: now,
      blocked: false,
      errorStatus: null,
      errorType: null
    });
    return true;
  }

  if (retryData.count >= MAX_RETRIES) {
    retryTracking.set(apiKey, {
      ...retryData,
      blocked: true,
      lastAttempt: now
    });
    return false;
  }

  retryTracking.set(apiKey, {
    count: retryData.count + 1,
    lastAttempt: now,
    blocked: false,
    errorStatus: errorStatus || retryData.errorStatus
  });

  return true;
};

export const getRetryDelay = (apiKey) => {
  const retryData = retryTracking.get(apiKey);
  if (!retryData) return MIN_RETRY_DELAY;
  
  const exponentialDelay = MIN_RETRY_DELAY * Math.pow(2, retryData.count - 1);
  return Math.min(exponentialDelay, MAX_RETRY_DELAY);
};

export const isApiBlocked = (apiKey) => {
  const retryData = retryTracking.get(apiKey);
  if (!retryData) return false;
  
  if (retryData.blocked) {
    const now = Date.now();
    const timeSinceBlock = now - retryData.lastAttempt;
    return timeSinceBlock < BLOCK_DURATION;
  }
  
  return false;
};

export const resetApiRetry = (apiKey) => {
  retryTracking.delete(apiKey);
  errorTypeTracking.delete(apiKey);
};

export const clearAllRetries = () => {
  retryTracking.clear();
  errorTypeTracking.clear();
};

export const getRetryInfo = (apiKey) => {
  return retryTracking.get(apiKey) || null;
};

export const isFatalError = (errorType) => {
  return FATAL_ERRORS.includes(errorType);
};

export const getBlockDuration = (errorType) => {
  return isFatalError(errorType) ? FATAL_ERROR_BLOCK_DURATION : BLOCK_DURATION;
};

export const markApiFailed = (apiKey, errorType = null) => {
  const now = Date.now();
  const blockDuration = errorType ? getBlockDuration(errorType) : BLOCK_DURATION;
  
  retryTracking.set(apiKey, {
    count: MAX_RETRIES,
    lastAttempt: now,
    blocked: true,
    errorType,
    blockUntil: now + blockDuration
  });
  
  if (errorType) {
    errorTypeTracking.set(apiKey, errorType);
  }
};
